import { useState, useEffect, useCallback } from "react";
import {
  Col, Row, Card, CardBody, Input, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { Button, CircularProgress, Box, Pagination, Tooltip } from "@mui/material";
import axiosInstance from "../../helpers/axios";
import "./PaymentMismatch.css";

const SEVERITY_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

const SeverityBadge = ({ value }) => {
  if (!value) return <span style={{ color: "#ccc" }}>—</span>;
  const cls = { CRITICAL: "badge-critical", HIGH: "badge-high", MEDIUM: "badge-medium", LOW: "badge-low" };
  return <span className={`badge-severity ${cls[value] ?? "badge-low"}`}>{value}</span>;
};

const StatusBadge = ({ value }) => {
  const cls = { pending: "badge-pending", authorised: "badge-authorised", suspicious: "badge-suspicious" };
  return <span className={`badge-status ${cls[value] ?? "badge-pending"}`}>{value}</span>;
};

const SummaryCard = ({ label, value, sub, color }) => (
  <div className="summary-card h-100">
    <div className="summary-label">{label}</div>
    <div className="summary-value" style={{ color: color ?? "#101828" }}>{value}</div>
    {sub && <div className="summary-sub">{sub}</div>}
  </div>
);

export default function PaymentMismatch() {
  const [records, setRecords]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [totalRows, setTotalRows]     = useState(0);
  const [page, setPage]               = useState(1);
  const rowsPerPage                   = 15;

  // Filters — default FROM is the 1st of the current month
  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterStatus, setFilterStatus]     = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [filterDateTo, setFilterDateTo]     = useState("");
  const [filterLoanId, setFilterLoanId]     = useState("");

  // Summary
  const [summary, setSummary] = useState({
    totalToday: 0, pendingReview: 0, highCritical: 0, autoCleared: 0,
  });

  // Detail modal
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [reviewNotes, setReviewNotes]       = useState("");
  const [actionLoading, setActionLoading]   = useState(false);

  const user = sessionStorage.getItem("user");

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: rowsPerPage,
        ...(filterSeverity && { severity: filterSeverity }),
        ...(filterStatus   && { status:   filterStatus }),
        ...(filterDateFrom && { from:     filterDateFrom }),
        ...(filterDateTo   && { to:       filterDateTo }),
        ...(filterLoanId   && { loanId:   filterLoanId }),
      });
      const res = await axiosInstance.get(`/payment-mismatch?${params}`);
      setRecords(res?.data?.data?.records ?? []);
      setTotalRows(res?.data?.data?.total ?? 0);
      setSummary(res?.data?.data?.summary ?? summary);
    } catch (e) {
      console.error("Failed to fetch mismatch records", e);
    } finally {
      setLoading(false);
    }
  }, [page, filterSeverity, filterStatus, filterDateFrom, filterDateTo, filterLoanId]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleUpdateStatus = async (id, status) => {
    setActionLoading(true);
    try {
      await axiosInstance.patch(`/payment-mismatch/${id}/status`, {
        status, reviewNotes,
      });
      setSelectedRecord(null);
      setReviewNotes("");
      fetchRecords();
    } catch (e) {
      console.error("Failed to update status", e);
    } finally {
      setActionLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterSeverity(""); setFilterStatus("");
    setFilterDateFrom(""); setFilterDateTo("");
    setFilterLoanId(""); setPage(1);
  };

  const totalPages = Math.ceil(totalRows / rowsPerPage);

  return (
    <div style={{ background: "#f9fafb", minHeight: "100vh" }}>
        <div style={{ padding: "24px 28px" }}>

          {/* ── Page Header ── */}
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ fontWeight: 700, color: "#101828", marginBottom: 4 }}>
              Payment Mismatch Monitor
            </h4>
            <p style={{ color: "#667085", fontSize: 13, margin: 0 }}>
              Payments where the payer's phone number does not match the borrower's registered number.
            </p>
          </div>

          {/* ── Summary Cards ── */}
          <Row className="mb-4 g-3">
            <Col md={3}>
              <SummaryCard label="Mismatches Today"  value={summary.totalToday}   sub="Last 24 hours" />
            </Col>
            <Col md={3}>
              <SummaryCard label="Pending Review"    value={summary.pendingReview} sub="Awaiting ops action" color="#b54708" />
            </Col>
            <Col md={3}>
              <SummaryCard label="High / Critical"   value={summary.highCritical}  sub="Needs immediate attention" color="#c01048" />
            </Col>
            <Col md={3}>
              <SummaryCard label="Auto-Cleared"      value={summary.autoCleared}   sub="Known contacts (family)" color="#027a48" />
            </Col>
          </Row>

          {/* ── Filter Bar ── */}
          <div className="filter-bar mb-3">
            <Row className="align-items-end g-2">
              <Col md={2}>
                <div className="filter-label">Loan ID</div>
                <Input bsSize="sm" placeholder="Search loan ID..."
                  value={filterLoanId}
                  onChange={e => { setFilterLoanId(e.target.value); setPage(1); }}
                  style={{ fontSize: 13 }}
                />
              </Col>
              <Col md={2}>
                <div className="filter-label">Severity</div>
                <Input type="select" bsSize="sm" value={filterSeverity}
                  onChange={e => { setFilterSeverity(e.target.value); setPage(1); }}
                  style={{ fontSize: 13 }}>
                  <option value="">All</option>
                  {SEVERITY_ORDER.map(s => <option key={s} value={s}>{s}</option>)}
                </Input>
              </Col>
              <Col md={2}>
                <div className="filter-label">Status</div>
                <Input type="select" bsSize="sm" value={filterStatus}
                  onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                  style={{ fontSize: 13 }}>
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="authorised">Authorised</option>
                  <option value="suspicious">Suspicious</option>
                </Input>
              </Col>
              <Col md={2}>
                <div className="filter-label">From Date</div>
                <Input type="date" bsSize="sm" value={filterDateFrom}
                  onChange={e => { setFilterDateFrom(e.target.value); setPage(1); }}
                  style={{ fontSize: 13 }} />
              </Col>
              <Col md={2}>
                <div className="filter-label">To Date</div>
                <Input type="date" bsSize="sm" value={filterDateTo}
                  onChange={e => { setFilterDateTo(e.target.value); setPage(1); }}
                  style={{ fontSize: 13 }} />
              </Col>
              <Col md={2} className="d-flex gap-2">
                <Button size="small" variant="outlined" onClick={clearFilters} title="Clear filters"
                  style={{
                    minWidth: 32, width: 32, height: 32, padding: 0,
                    borderColor: "#d0d5dd", color: "#667085", fontSize: 16, lineHeight: 1,
                  }}>
                  ×
                </Button>
                <Button size="small" variant="contained" onClick={fetchRecords}
                  style={{ fontSize: 12, textTransform: "none", background: "#D32028" }}>
                  Apply
                </Button>
              </Col>
            </Row>
          </div>

          {/* ── Table ── */}
          <Card className="tables">
            <CardBody style={{ padding: 0 }}>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                  <CircularProgress style={{ color: "#D32028" }} />
                </Box>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr className="table-heading">
                        <th>Loan ID</th>
                        <th>Borrower Phone</th>
                        <th>Payer Phone / VPA</th>
                        <th>Amount</th>
                        <th>DPD</th>
                        <th>Payer Identity</th>
                        <th>Severity</th>
                        <th>Status</th>
                        <th>Paid At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {records.length === 0 ? (
                        <tr>
                          <td colSpan={10} style={{ textAlign: "center", color: "#667085", padding: "40px 0" }}>
                            No mismatch records found.
                          </td>
                        </tr>
                      ) : records.map((rec) => (
                        <tr key={rec.id} className="table-row"
                          onClick={() => { setSelectedRecord(rec); setReviewNotes(rec.reviewNotes ?? ""); }}>
                          <td style={{ fontWeight: 600, color: "#D32028" }}>{rec.loanId}</td>
                          <td>
                            <span className="phone-match">{rec.borrowerPhone}</span>
                          </td>
                          <td>
                            <div className={rec.isMismatch ? "phone-mismatch" : "phone-match"}>
                              {rec.payerPhone}
                            </div>
                            {rec.payerVpa && (
                              <div style={{ fontSize: 11, color: "#667085" }}>{rec.payerVpa}</div>
                            )}
                          </td>
                          <td style={{ fontWeight: 600 }}>
                            ₹{Number(rec.amount).toLocaleString("en-IN")}
                          </td>
                          <td>
                            <span style={{ color: rec.dpd > 0 ? "#c01048" : "#027a48", fontWeight: 600 }}>
                              {rec.dpd}
                            </span>
                          </td>
                          <td>
                            {rec.payerIsKnownContact ? (
                              <Tooltip title={`${rec.payerContactName} · ${rec.payerContactRelation}`} arrow>
                                <span className="known-contact-tag">
                                  {rec.payerContactRelation ?? "Known"}
                                </span>
                              </Tooltip>
                            ) : rec.payerHasActiveLoan ? (
                              <Tooltip title={`Has active loan(s): ${rec.payerLoanIds}`} arrow>
                                <span className="badge-severity badge-high">Cross-Loan</span>
                              </Tooltip>
                            ) : (
                              <span className="unknown-tag">Unknown</span>
                            )}
                          </td>
                          <td><SeverityBadge value={rec.severity} /></td>
                          <td><StatusBadge value={rec.status} /></td>
                          <td style={{ fontSize: 12, color: "#667085" }}>{rec.paidAt}</td>
                          <td onClick={e => e.stopPropagation()}>
                            {rec.status === "pending" && (
                              <>
                                <button className="btn-authorise"
                                  onClick={() => handleUpdateStatus(rec.id, "authorised")}>
                                  ✓ Clear
                                </button>
                                <button className="btn-flag"
                                  onClick={() => handleUpdateStatus(rec.id, "suspicious")}>
                                  ⚑ Flag
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #eaecf0" }}>
                  <span style={{ fontSize: 13, color: "#667085" }}>
                    {totalRows} total records
                  </span>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    size="small"
                    shape="rounded"
                    sx={{ "& .MuiPaginationItem-root.Mui-selected": { background: "#D32028", color: "#fff" } }}
                  />
                </div>
              )}
            </CardBody>
          </Card>

        </div>

      {/* ── Detail Modal ── */}
      <Modal isOpen={!!selectedRecord} toggle={() => setSelectedRecord(null)} size="lg" centered>
        {selectedRecord && (
          <>
            <div className="mismatch-modal-header d-flex justify-content-between align-items-center">
              <span>Mismatch Detail — {selectedRecord.loanId}</span>
              <SeverityBadge value={selectedRecord.severity} />
            </div>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#667085", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Payment Info
                  </div>
                  {[
                    ["Loan ID",         selectedRecord.loanId],
                    ["Borrower Phone",  selectedRecord.borrowerPhone],
                    ["Payer Phone",     selectedRecord.payerPhone],
                    ["Payer VPA",       selectedRecord.payerVpa ?? "—"],
                    ["Amount",          `₹${Number(selectedRecord.amount).toLocaleString("en-IN")}`],
                    ["Payment Method",  selectedRecord.paymentMethod],
                    ["Paid At",         selectedRecord.paidAt],
                    ["DPD at Payment",  selectedRecord.dpd],
                  ].map(([k, v]) => (
                    <div className="detail-row" key={k}>
                      <span className="detail-key">{k}</span>
                      <span className="detail-value">{v}</span>
                    </div>
                  ))}
                </Col>
                <Col md={6}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#667085", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Risk Context
                  </div>
                  {[
                    ["Known Contact?",     selectedRecord.payerIsKnownContact ? `Yes — ${selectedRecord.payerContactName} (${selectedRecord.payerContactRelation})` : "No"],
                    ["Has Active Loan?",   selectedRecord.payerHasActiveLoan ? `Yes — ${selectedRecord.payerLoanIds}` : "No"],
                    ["Borrower Notified?", selectedRecord.borrowerNotified ? "Yes" : "No"],
                    ["Current Status",     selectedRecord.status],
                  ].map(([k, v]) => (
                    <div className="detail-row" key={k}>
                      <span className="detail-key">{k}</span>
                      <span className="detail-value">{v}</span>
                    </div>
                  ))}

                  {/* SMS Preview */}
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: "#667085", marginBottom: 6 }}>
                      SMS SENT TO BORROWER
                    </div>
                    <div className="sms-preview">
                      Dear <strong>{selectedRecord.borrowerName} (Borrower)</strong>,<br />
                      A payment of ₹{Number(selectedRecord.amount).toLocaleString("en-IN")} has been received by Feemonk towards your EMI for Loan ID: {selectedRecord.loanId}.<br />
                      Paid via UPI: {selectedRecord.payerVpa} | {selectedRecord.paidAt}<br />
                      {!selectedRecord.payerIsKnownContact
                        ? "If this payment was not made by you or someone you know, contact us immediately at hello@feemonk.com / 9100820555."
                        : "For queries: hello@feemonk.com / 9100820555."}
                    </div>
                  </div>

                  {/* Review Notes */}
                  {selectedRecord.status === "pending" && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: "#667085", marginBottom: 6 }}>
                        REVIEW NOTES (optional)
                      </div>
                      <Input type="textarea" rows={2} placeholder="e.g. Borrower's father — confirmed via call"
                        value={reviewNotes}
                        onChange={e => setReviewNotes(e.target.value)}
                        style={{ fontSize: 13 }}
                      />
                    </div>
                  )}
                  {selectedRecord.reviewNotes && selectedRecord.status !== "pending" && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: "#667085", marginBottom: 6 }}>REVIEW NOTES</div>
                      <div style={{ fontSize: 13, color: "#344054", background: "#f9fafb", borderRadius: 6, padding: "10px 12px", border: "1px solid #eaecf0" }}>
                        {selectedRecord.reviewNotes}
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter style={{ borderTop: "1px solid #eaecf0", gap: 8 }}>
              <Button variant="outlined" size="small"
                onClick={() => setSelectedRecord(null)}
                style={{ textTransform: "none", fontSize: 13, borderColor: "#d0d5dd", color: "#344054" }}>
                Close
              </Button>
              {selectedRecord.status === "pending" && (
                <>
                  <Button variant="contained" size="small" disabled={actionLoading}
                    onClick={() => handleUpdateStatus(selectedRecord.id, "authorised")}
                    style={{ textTransform: "none", fontSize: 13, background: "#027a48" }}>
                    ✓ Mark Authorised
                  </Button>
                  <Button variant="contained" size="small" disabled={actionLoading}
                    onClick={() => handleUpdateStatus(selectedRecord.id, "suspicious")}
                    style={{ textTransform: "none", fontSize: 13, background: "#c01048" }}>
                    ⚑ Flag Suspicious
                  </Button>
                </>
              )}
            </ModalFooter>
          </>
        )}
      </Modal>
    </div>
  );
}
